import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { _t } from '@/locale';
import { useHistory } from 'react-router-dom';

import { classnames } from '@/helpers';
import useReviews, { REVIEWS_MODAL_LIMIT } from '@/hooks/useReviews';
import type { SelectedTimeFilterOptions } from '@/types/reviews';
import { Employee, Place } from '@/types/api/services/booking/schema';
import { ReviewsModalType } from '@/types/modal';
import Card from '@/components/elements/Card/Card';
import { BOOKING_FLOW_REDESIGN_FLAG, REVIEWS_V2_LISTCARDS_V2_FLAG } from '@/constants/experimentConstants';
import { ROUTE_BUILDER, toSlugId } from '@/constants/pages';
import { safeEncodeURIComponent } from '@/helpers/urlTs';
import { useConfidenceFlag } from '@/hooks/useConfidenceFlag';

import { Reviews } from '@/features/reviews/Reviews';
import { ReviewsServiceDetails } from '@/features/reviews/ReviewsServiceDetails';
import { ReviewsEmployeeDetails } from '@/features/reviews/ReviewsEmployeeDetails';
import { ModalContent, ModalDialog, ModalFooter, ModalHeader } from '@/components/modules/modals';
import { ReviewEmployeeSocials } from './ReviewEmployeeSocials';
import { Service } from '@/types/services';
import { TIME_FILTER } from '@/constants/timeConstants';

type ModalProps = {
  placeId: number;
  tooltipTargetRef?: React.RefObject<HTMLDivElement>;
  hideStaff?: boolean;
  isMpReviews?: boolean;
  context?: 'placePage' | 'bookingFlow';
  /**
   * Replaces the service footer's redirect for hosts that book in place (the booking flow).
   * Returning undefined for a service keeps the default redirect. `employeeId` is the employee
   * picked in the modal, if any — the default redirect carries it.
   */
  getServiceCta?: (serviceId: number, employeeId?: number) => { label: string; onClick: () => void } | undefined;
};

export const ReviewsModalContent = ({ placeId, isMpReviews, getServiceCta, context, hideStaff }: ModalProps) => {
  const modalUid = `${placeId}_modal`;
  const history = useHistory();

  const reviewModal = useSelector(
    (state: {
      modal: {
        reviews: ReviewsModalType;
      };
    }) => state.modal.reviews || {},
  );

  const flags = useSelector((state: { flags: Record<string, unknown> }) => state.flags || {});
  const bookingFlowRedesign = flags[BOOKING_FLOW_REDESIGN_FLAG.name] as { payload?: { enabled?: boolean } };

  const { flagValue: reviewsV2FlagValue } = useConfidenceFlag(
    REVIEWS_V2_LISTCARDS_V2_FLAG.name,
    REVIEWS_V2_LISTCARDS_V2_FLAG.defaultValue,
    true,
  );

  const isReviewsV2Enabled = Boolean(reviewsV2FlagValue?.['reviews-v2']);

  const initialSearchFilter = {
    placeId,
    page: 1,
    isMpReviews,
    limit: reviewModal.limit || REVIEWS_MODAL_LIMIT,
    serviceId: reviewModal.serviceId,
    employeeId: reviewModal.employeeId,
    hasMessage: true,
    rating: reviewModal.rating || undefined,
    timeFilter: reviewModal.timeFilter || (TIME_FILTER.FOREVER as SelectedTimeFilterOptions),
  };

  const {
    searchFilter: searchFilterFromState,
    openReviewsModal,
    setRatingFilter,
    setEmployeeServiceFilter,
    switchFocus,
    resetState,
    navigateToPreviousFocus,
    handleLoadMore,
    searchResult,
    setPrevFocusState,
  } = useReviews({
    uid: modalUid,
    originType: 'reviewsModal',
    searchFilter: initialSearchFilter,
    initiateState: true,
  });

  useEffect(() => {
    return () => {
      resetState();
    };
    // eslint-disable-next-line
  }, []);

  const placeAbout = useSelector((state: { place: Record<number, Place> }) => state.place[placeId]?.about || {});

  const service = useSelector(
    (state: { services: { all: Record<number, Service> } }) => state.services.all[searchFilterFromState?.serviceId],
  );
  const employee = useSelector(
    (state: { employees: { all: Record<number, Employee> } }) => state.employees.all[searchFilterFromState?.employeeId],
  );

  const onEmployeeListItemClicked = (employeeId: number, serviceId: number) => {
    setEmployeeServiceFilter(employeeId, serviceId);
  };

  const setReviewModal = (data: ReviewsModalType) => {
    openReviewsModal({ show: data.show, placeId, ...data });
  };

  const onShowMoreClick = () => {
    handleLoadMore(REVIEWS_MODAL_LIMIT);
  };

  const onNavigate = ({
    focus,
    employeeId,
    serviceId,
  }: {
    focus: 'employee' | 'service' | 'rating';
    employeeId?: number;
    serviceId?: number;
  }) => {
    const prevState = {
      id: Date.now(),
      focus: reviewModal.focus,
      employeeId: searchFilterFromState.employeeId,
      serviceId: searchFilterFromState.serviceId,
      limit: searchFilterFromState.limit,
      rating: searchFilterFromState.rating,
      timeFilter: searchFilterFromState.timeFilter,
    };

    setPrevFocusState(prevState);
    switchFocus({ focus, employeeId, serviceId });
  };

  const getContent = () => {
    if (reviewModal.focus === 'employee' && searchFilterFromState?.employeeId) {
      return (
        <>
          <ReviewsEmployeeDetails
            placeId={placeId}
            employeeId={searchFilterFromState?.employeeId}
            reviewsUid={modalUid}
            className={'mb-6'}
          />
          <ReviewEmployeeSocials employeeId={searchFilterFromState?.employeeId} withCard className={'mb-4'} />
          {isReviewsV2Enabled ? (
            <Card className={'mb-4'}>
              <Reviews
                placeId={placeId}
                onServiceClick={
                  context !== 'bookingFlow'
                    ? (employeeId: number, serviceId: number) => {
                        onNavigate({ focus: 'service', employeeId, serviceId });
                      }
                    : undefined
                }
                focus={reviewModal.focus}
                onRatingClick={setRatingFilter}
                onShowMoreClick={onShowMoreClick}
                uid={modalUid}
                originType={'reviewsModal'}
              />
            </Card>
          ) : null}
        </>
      );
    }

    if (reviewModal.focus === 'service' && searchFilterFromState?.serviceId) {
      return (
        <>
          <ReviewsServiceDetails
            serviceId={searchFilterFromState?.serviceId}
            employeeId={searchFilterFromState?.employeeId}
            hideStaff={hideStaff}
            onEmployeeClick={
              context !== 'bookingFlow'
                ? (employeeId: number, serviceId: number) => {
                    onEmployeeListItemClicked(employeeId, serviceId);
                  }
                : undefined
            }
            uid={modalUid}
          />
          {isReviewsV2Enabled ? (
            <Card>
              <Reviews
                placeId={placeId}
                onEmployeeClick={
                  context !== 'bookingFlow'
                    ? (employeeId: number) => {
                        onNavigate({ focus: 'employee', employeeId, serviceId: undefined });
                      }
                    : undefined
                }
                focus={reviewModal.focus}
                onRatingClick={setRatingFilter}
                onShowMoreClick={onShowMoreClick}
                uid={modalUid}
                originType={'reviewsModal'}
              />
            </Card>
          ) : null}
        </>
      );
    }

    if (reviewModal.focus === 'rating') {
      return (
        <Reviews
          placeId={placeId}
          onEmployeeClick={(employeeId: number, serviceId: number) => {
            onNavigate({ focus: 'employee', employeeId, serviceId: undefined });
          }}
          onServiceClick={(employeeId: number, serviceId: number) => {
            onNavigate({ focus: 'service', employeeId, serviceId });
          }}
          focus={reviewModal.focus}
          onRatingClick={setRatingFilter}
          onShowMoreClick={onShowMoreClick}
          uid={modalUid}
          originType={'reviewsModal'}
        />
      );
    }

    return null;
  };

  const getModalTitle = () => {
    switch (reviewModal.focus) {
      case 'employee':
        return _t('aboutTheEmployee');
      case 'service':
        return _t('aboutTheService');
      default:
        return _t('reviews');
    }
  };

  const closedModalProps = {
    show: false,
    placeId: 0,
    serviceId: 0,
    employeeId: 0,
    rating: '',
    timeFilter: TIME_FILTER.FOREVER as SelectedTimeFilterOptions,
    // The reducer merges, so an opener that omits context would otherwise inherit the last one.
    context: undefined,
  };

  const withFooter = reviewModal.focus === 'service' || reviewModal.focus === 'employee';

  const serviceCta =
    reviewModal.focus === 'service' && searchFilterFromState?.serviceId
      ? getServiceCta?.(searchFilterFromState.serviceId, searchFilterFromState.employeeId)
      : undefined;

  const buttonTitle = (() => {
    if (reviewModal.focus === 'service') {
      return serviceCta?.label ?? _t('BookService');
    }

    if (reviewModal.focus === 'employee' && employee) {
      return _t('BookEmployee', {
        name: employee.about.name,
      });
    }
    return null;
  })();

  const getRedirectLink = () => {
    const srParameter = !!employee ? safeEncodeURIComponent(employee.about.externalId) : '0';

    // New booking flow
    if (bookingFlowRedesign?.payload?.enabled) {
      if (reviewModal.focus === 'service' && !!service && !!placeAbout) {
        const route = `${ROUTE_BUILDER.bookingCalendar(
          toSlugId(placeAbout.slug, placeId),
          toSlugId(service.about.slug, service.id),
        )}?sr=${srParameter}&ss=${safeEncodeURIComponent(service.id.toString())}`;
        return route;
      }

      if (reviewModal.focus === 'employee' && !!employee && !!placeAbout) {
        const route = `${ROUTE_BUILDER.bookingFlowSelectEmployee(
          toSlugId(placeAbout.slug, placeId),
        )}?sr=${srParameter}`;
        return route;
      }
    }

    if (reviewModal.focus === 'employee' && !!employee && !!placeAbout) {
      const route = `${ROUTE_BUILDER.placePage(toSlugId(placeAbout.slug, placeId))}?sr=${srParameter}`;

      return route;
    }

    if (reviewModal.focus === 'service' && !!service && !!placeAbout) {
      const route = `${ROUTE_BUILDER.placePage(
        toSlugId(placeAbout.slug, placeId),
      )}?sr=${srParameter}&ss=${safeEncodeURIComponent(service.id.toString())}`;

      return route;
    }

    return '';
  };

  const onFooterButtonClick = () => {
    if (serviceCta) {
      serviceCta.onClick();
      setReviewModal({ ...closedModalProps });
      return;
    }

    const newRoute = getRedirectLink();
    setReviewModal({ ...closedModalProps });
    if (newRoute) {
      history.push(newRoute);
    }
  };

  return (
    <ModalDialog
      appElement={document.getElementById('root')}
      isOpen
      onRequestClose={() => setReviewModal({ ...closedModalProps })}>
      <ModalContent size="lg" stickyHeader>
        <ModalHeader
          onClose={() => setReviewModal({ ...closedModalProps })}
          onBack={
            reviewModal.prevState?.length
              ? () => {
                  if (searchResult.loading) {
                    return;
                  }
                  navigateToPreviousFocus();
                }
              : undefined
          }
          variant={'primary'}
          title={getModalTitle()}
          position="sticky"
        />
        <div
          className={classnames({
            'bg-primary-200': withFooter,
            'p-xl': true,
            'flex-1': true,
          })}>
          {getContent()}
        </div>
        {withFooter ? (
          <ModalFooter
            onCloseClick={() => setReviewModal({ ...closedModalProps })}
            onCtaClick={onFooterButtonClick}
            ctaLabel={buttonTitle}
            position="sticky"
          />
        ) : null}
      </ModalContent>
    </ModalDialog>
  );
};

const FALLBACK_STATE = { show: false };

export const ReviewsModal = ({ placeId, tooltipTargetRef, hideStaff, isMpReviews, getServiceCta }: ModalProps) => {
  const reviewModal = useSelector(
    (state: {
      modal: {
        reviews: {
          show: boolean;
          placeId: number;
          serviceId?: string;
          employeeId?: string;
          context?: 'placePage' | 'bookingFlow';
        };
      };
    }) => state.modal.reviews || FALLBACK_STATE,
  ) as ReviewsModalType;

  if (!reviewModal.show) {
    return null;
  }

  return (
    <ReviewsModalContent
      placeId={placeId}
      context={reviewModal.context}
      tooltipTargetRef={tooltipTargetRef}
      hideStaff={hideStaff}
      isMpReviews={isMpReviews}
      getServiceCta={getServiceCta}
    />
  );
};
